/**
 * Remote players.
 *
 * ## What this file is, after the crowd rewrite
 *
 * A remote player used to be an `Object3D` tree — a group, a capsule, a visor slab, a canvas
 * `Sprite` nameplate and, once its GLB landed, a `SkinnedMesh` with ~112 bones and an
 * `AnimationMixer`. Seventeen of those put an RTX 4080 at 11 fps (`docs/WEBGPU-NOTES.md` §6),
 * and a city built for five hundred people cannot pay per-character CPU at all.
 *
 * So a remote player is now **three things and no objects**:
 *
 *  1. a slot id in `render/crowd.ts` — the body, its gear, its blob shadow and its nameplate,
 *     all of it sixteen floats in a texture;
 *  2. the interpolation ring, which is unchanged and hard-won: 100 ms behind the server clock,
 *     lerped between the two bracketing snapshots, extrapolated on the last known velocity for
 *     at most 100 ms when the packets stop;
 *  3. the pose history the kill cam replays.
 *
 * Nothing here constructs a `Group`, a `Mesh`, a `Sprite`, a `SkinnedMesh`, a `Bone` or an
 * `AnimationMixer`. If a feature needs per-character state it goes in the crowd's state
 * texture — see rule 1 of `docs/PERF-BUDGET.md`.
 *
 * ## What went with the objects
 *
 * - **Corpses.** `CorpseField` existed to reparent a dead rig into a standalone group so the
 *   respawned player could get a fresh one. A crowd slot needs none of that: `AnimState.Dead`
 *   is a non-looping clip that the field clamps on its last frame, so a dead body simply lies
 *   in its own slot until the server moves it. `clearCorpses()` survives as a no-op because
 *   main.ts calls it on Welcome.
 * - **The `?chars=0` bisect.** The crowd *is* the character path now; `?crowd=N` in
 *   `docs/PERF-BUDGET.md` is the switch that means something.
 * - **The Veil shimmer and the downed beacon sprite.** `SnapFlags.Veiled` no longer exists in
 *   v3, and the amber pillar was a per-player `Sprite`. Downed players are found through
 *   `nearestDowned()` and the HUD, not through a light in the world.
 */
import {
  INTERP_DELAY_MS,
  PLAYER_HEIGHT,
  SnapFlags,
  Stance,
  WALK_SPEED,
  clamp,
  lerp,
  lerpAngle,
} from '@stillsun/shared';
import type { HitTarget, PlayerSnap, Vec3 } from '@stillsun/shared';
import type { CrowdField } from '../render/crowd.js';

const RING = 24;
/**
 * A second, longer ring kept purely for the kill cam: 128 snapshots is ~4.3 s at 30 Hz,
 * comfortably more than the 2.5 s replay so a packet hitch never shortens it. It is a
 * preallocated array of mutable structs — `push` writes fields, it never allocates.
 */
const HISTORY = 128;
const MAX_EXTRAPOLATE_MS = 100;
/**
 * No snapshot for this long and the body is dropped.
 *
 * `PlayerLeft` and a shortened `setRoster` are not enough on their own: snapshots are interest
 * filtered (`TARGET_KEEP_RANGE` and the server's relevance radius), so somebody who walks out
 * of range stops appearing in snapshots while staying in the room *and* on the roster. Under
 * the old code that leaked an `Object3D`; it now leaks a crowd slot out of a pool of 512, and
 * an empty pool means `acquire()` returns -1 and nobody in the city gets a body. Same number
 * and same reason as `EntityManager`'s.
 */
const STALE_MS = 3000;

/** Below this ground speed a body is standing still as far as the clip clock is concerned. */
const MOVE_EPSILON = 0.4;

/** `look` bits 3..5 are the Calling — see `packLook` in `packages/shared/src/protocol.ts`. */
const LOOK_CALLING_SHIFT = 3;
const LOOK_CALLING_MASK = 0b111;

interface Sample {
  t: number; // server time, ms
  pos: Vec3;
  vel: Vec3;
  yaw: number;
  /** `AnimState`, straight off the wire. */
  anim: number;
  flags: number;
}

/**
 * One replayable pose. Flat scalars rather than a nested `Vec3` so the ring can be
 * preallocated once and only ever written field-by-field.
 *
 * `pitch` has no source on the v3 wire — the camera is the client's own and never crosses it
 * — so it is written 0 for every remote pose and only the kill cam's *local* recorder still
 * fills it. `stance` is derived from `SnapFlags.Crouching`, which is what the hit capsule
 * needs (`targetHeight()` in `shared/hitscan.ts`).
 */
export interface PoseSample {
  /** Server clock, ms — the same stamp `RemoteManager.onSnapshot` is handed. */
  t: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Always 0 for a remote pose; the kill cam's own recorder still writes a real one. */
  pitch: number;
  /** constants.Stance, derived from `SnapFlags.Crouching`. */
  stance: number;
  /** `AnimState` at this pose, so a replayed body can play what it was actually doing. */
  anim: number;
  flags: number;
}

export function newPoseSample(): PoseSample {
  return { t: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0, stance: Stance.Stand, anim: 0, flags: 0 };
}

/** Copies `src` into `dst` by value — replay buffers must never alias a live ring. */
export function copyPose(src: PoseSample, dst: PoseSample): void {
  dst.t = src.t;
  dst.x = src.x;
  dst.y = src.y;
  dst.z = src.z;
  dst.yaw = src.yaw;
  dst.pitch = src.pitch;
  dst.stance = src.stance;
  dst.anim = src.anim;
  dst.flags = src.flags;
}

/** `SnapFlags.Crouching` -> the stance the hit capsule is sized from. */
function stanceOf(flags: number): number {
  return (flags & SnapFlags.Crouching) !== 0 ? Stance.Crouch : Stance.Stand;
}

/**
 * One walk cycle serves every speed: the clip clock is scaled rather than a blend tree built.
 * Below a stroll it is pinned to 1 so an idle never crawls. Mirrors the local body's formula
 * in main.ts exactly, so a remote and the player next to them step at the same rate.
 */
function speedFromVelocity(vx: number, vz: number): number {
  const h = Math.hypot(vx, vz);
  return h > MOVE_EPSILON ? clamp(h / WALK_SPEED, 0.5, 2) : 1;
}

/**
 * Hand a slot back the way it was taken.
 *
 * `CrowdField.acquire()` resets the clip id, the clip clock, the clip speed, the hit flash and
 * the name — but **not** the scale or the tint, which are written once in the field's
 * constructor and never again. A slot released at a monster's ash tint or an NPC's `bodyScale`
 * therefore poisons the next acquirer, and the next acquirer is very often a player. Every
 * release in this package goes through here (see `game/entities.ts` and `game/npcs.ts` for the
 * other two) rather than calling `release()` raw.
 */
export function releaseSlot(crowd: CrowdField, slot: number): void {
  if (slot < 0) return;
  crowd.setName(slot, null);
  crowd.setScale(slot, 1);
  crowd.setTint(slot, 1, 1, 1);
  crowd.release(slot);
}

class RemotePlayer {
  readonly id: number;
  name: string;
  /** Crowd slot, or -1 when the field is full or was never loaded. */
  private slot = -1;
  private readonly crowd: CrowdField | null;
  private readonly samples: Sample[] = [];
  /** Kill-cam pose ring (see HISTORY). Preallocated; `histWrites` is monotonic. */
  private readonly history: PoseSample[] = [];
  private histWrites = 0;
  /**
   * Kill cam: while we sit inside this player's replayed head their live body would be
   * walking around in front of us. Scaled to nothing for the duration of the replay — a
   * released slot could be taken by somebody else and never come back.
   */
  private isHidden = false;
  /** Server clock of the last snapshot that named this player. See `STALE_MS`. */
  lastSeen = 0;
  private dead = false;
  /** Last rendered SnapFlags, so downed/crouch reads never need the sample ring again. */
  private flags = 0;
  /** Latest `PlayerSnap.look`; -1 until the first snapshot, so the first word always writes. */
  private lookWord = -1;
  private levelSeen = 0;
  private hpSeen = 0;
  private maxHpSeen = 1;
  /**
   * `Ev.Cast` carries the **full** length of a cast once, at its start (v3.1 §9.2), so the
   * client animates the bar itself: the end is a local clock reading, not a countdown anybody
   * has to remember to tick. An interrupt arrives as a second Cast with `castMs: 0`.
   */
  private castEndAt = 0;
  private castSpan = 1;
  /** The ability slot that cast belongs to; meaningless once the cast has run out. */
  private castSlotSeen = 0;
  /** The interpolated transform this body was last drawn at. The only "position" it has. */
  private rx = 0;
  private ry = 0;
  private rz = 0;
  private ryaw = 0;
  private readonly velScratch: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly posScratch: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(id: number, name: string, crowd: CrowdField | null) {
    this.id = id;
    this.name = name;
    this.crowd = crowd;
    for (let i = 0; i < HISTORY; i++) this.history.push(newPoseSample());
    if (crowd) {
      this.slot = crowd.acquire();
      if (this.slot >= 0) crowd.setName(this.slot, name);
    }
  }

  setName(name: string): void {
    if (name === this.name) return;
    this.name = name;
    if (this.crowd && this.slot >= 0 && !this.isHidden) this.crowd.setName(this.slot, name);
  }

  /** The kill cam's killer: their live body must not be inside the camera we parked in it. */
  setHidden(on: boolean): void {
    if (on === this.isHidden) return;
    this.isHidden = on;
    if (!this.crowd || this.slot < 0) return;
    // Scale, not release: `acquire()` can return -1 in a full city and the body would then
    // never come back. A zero scale takes the blob shadow with it (`blobMaterial` multiplies
    // BLOB_RADIUS by the same float), and the plate goes with the name.
    this.crowd.setScale(this.slot, on ? 0 : 1);
    this.crowd.setName(this.slot, on ? null : this.name);
  }

  get hidden(): boolean {
    return this.isHidden;
  }

  push(t: number, snap: PlayerSnap): void {
    this.lastSeen = t;
    const last = this.samples.at(-1);
    if (last && t <= last.t) return; // out-of-order or duplicate
    this.samples.push({
      t,
      pos: { ...snap.pos },
      vel: { ...snap.vel },
      yaw: snap.yaw,
      anim: snap.anim,
      flags: snap.flags,
    });
    if (this.samples.length > RING) this.samples.splice(0, this.samples.length - RING);

    this.levelSeen = snap.level;
    // Health is on the wire for every player since v3 (§3.1) and the target frame reads it
    // off whoever is selected; a remote's own bar is still the nameplate's.
    this.hpSeen = snap.hp;
    this.maxHpSeen = Math.max(1, snap.maxHp);
    // The look word changes mid-session now (equipping a helm is a new `look`), so it is
    // compared rather than set once — `setLook` re-derives the race and rebuilds the gear
    // read, and doing that 30 times a second for 500 players would be the whole frame.
    if (snap.look !== this.lookWord) {
      this.lookWord = snap.look;
      if (this.crowd && this.slot >= 0) this.crowd.setLook(this.slot, snap.look);
    }
    // Ruined kit greys the body (v3.2 `SnapFlags.Broken`). `setBroken` is a no-op when the state
    // has not changed, so this costs a compare per player per snapshot and nothing else.
    if (this.crowd && this.slot >= 0)
      this.crowd.setBroken(this.slot, (snap.flags & SnapFlags.Broken) !== 0);

    const h = this.history[this.histWrites % HISTORY];
    if (h) {
      h.t = t;
      h.x = snap.pos.x;
      h.y = snap.pos.y;
      h.z = snap.pos.z;
      h.yaw = snap.yaw;
      h.pitch = 0;
      h.stance = stanceOf(snap.flags);
      h.anim = snap.anim;
      h.flags = snap.flags;
      this.histWrites += 1;
    }
  }

  /**
   * Throws away the interpolation window. Called on a respawn: the ring still holds samples
   * from where they fell, and without this `apply()` would lerp the body across the map for
   * the whole 100 ms interpolation delay — a corpse sliding to the graveyard.
   */
  resetInterpolation(): void {
    this.samples.length = 0;
  }

  /** Server time of the newest pose we hold, or 0 if we have never seen this player. */
  get latestPoseTime(): number {
    if (this.histWrites === 0) return 0;
    return this.history[(this.histWrites - 1) % HISTORY]?.t ?? 0;
  }

  /**
   * Copies every pose stamped at or after `sinceMs` into `out` (oldest first), growing
   * `out` only the first time it is asked for more slots than it has. Returns the count.
   * Copies by value: the caller's buffer must survive the ring being overwritten.
   */
  copyHistory(sinceMs: number, out: PoseSample[]): number {
    const n = Math.min(this.histWrites, HISTORY);
    const base = this.histWrites - n;
    let count = 0;
    for (let i = 0; i < n; i++) {
      const src = this.history[(base + i) % HISTORY];
      if (!src || src.t < sinceMs) continue;
      let dst = out[count];
      if (!dst) {
        dst = newPoseSample();
        out.push(dst);
      }
      copyPose(src, dst);
      count += 1;
    }
    return count;
  }

  /** Places the body for the given render time (server clock, ms). */
  apply(renderTime: number): void {
    const n = this.samples.length;
    if (n === 0) return;

    const first = this.samples[0];
    const last = this.samples.at(-1);
    if (!first || !last) return;

    if (n === 1 || renderTime <= first.t) {
      this.place(first.pos, first.vel, first.yaw, first.anim, first.flags);
      return;
    }

    if (renderTime >= last.t) {
      // Starved: extrapolate with the last known velocity, bounded.
      const ahead = Math.min(renderTime - last.t, MAX_EXTRAPOLATE_MS) / 1000;
      const p = this.posScratch;
      p.x = last.pos.x + last.vel.x * ahead;
      p.y = last.pos.y + last.vel.y * ahead;
      p.z = last.pos.z + last.vel.z * ahead;
      this.place(p, last.vel, last.yaw, last.anim, last.flags);
      return;
    }

    for (let i = n - 1; i > 0; i--) {
      const b = this.samples[i];
      const a = this.samples[i - 1];
      if (!a || !b) continue;
      if (a.t <= renderTime && renderTime <= b.t) {
        const span = b.t - a.t;
        const t = span > 0 ? (renderTime - a.t) / span : 0;
        const p = this.posScratch;
        p.x = lerp(a.pos.x, b.pos.x, t);
        p.y = lerp(a.pos.y, b.pos.y, t);
        p.z = lerp(a.pos.z, b.pos.z, t);
        const v = this.velScratch;
        v.x = lerp(a.vel.x, b.vel.x, t);
        v.y = lerp(a.vel.y, b.vel.y, t);
        v.z = lerp(a.vel.z, b.vel.z, t);
        // Nearest sample for the discrete fields. v2 OR'd the single-tick `Firing` bit across
        // the window so a burst kept its muzzle flashes; v3 has no such bit — every flag left
        // in `SnapFlags` is a state that lasts many ticks, and `anim` is chosen server-side
        // once per snapshot, so rounding is exactly right for both.
        this.place(
          p,
          v,
          lerpAngle(a.yaw, b.yaw, t),
          t < 0.5 ? a.anim : b.anim,
          t < 0.5 ? a.flags : b.flags,
        );
        return;
      }
    }

    this.place(last.pos, last.vel, last.yaw, last.anim, last.flags);
  }

  /**
   * The whole render step: four floats and a clip id into the crowd's state texture.
   *
   * Health is not drawn on the body at all — the target frame in the HUD is where an MMO
   * reads a health bar, and a per-player bar was two `Sprite`s and two draw calls each.
   */
  private place(pos: Vec3, vel: Vec3, yaw: number, anim: number, flags: number): void {
    this.rx = pos.x;
    this.ry = pos.y;
    this.rz = pos.z;
    this.ryaw = yaw;
    this.flags = flags;
    this.dead = (flags & SnapFlags.Dead) !== 0;

    if (!this.crowd || this.slot < 0) return;
    // A death clip does not loop, so the field clamps it on its last frame and the body lies
    // where it fell for as long as the server keeps sending it. That is the whole corpse
    // system: no reparenting, no second rig, no pool.
    this.crowd.setState(this.slot, pos, yaw, anim, speedFromVelocity(vel.x, vel.z));
  }

  /** A hit: the crowd washes the body toward white and back over a fifth of a second. */
  onDamage(): void {
    if (this.crowd && this.slot >= 0) this.crowd.hitFlash(this.slot);
  }

  get isDowned(): boolean {
    return (this.flags & SnapFlags.Downed) !== 0 && !this.dead;
  }

  get isDead(): boolean {
    return this.dead;
  }

  get level(): number {
    return this.levelSeen;
  }

  get hp(): number {
    return this.hpSeen;
  }

  get maxHp(): number {
    return this.maxHpSeen;
  }

  /** `Ev.Cast`: `castMs` is the whole cast, and 0 clears the bar (an interrupt). */
  onCast(slot: number, castMs: number, nowMs: number): void {
    if (castMs <= 0) {
      this.castEndAt = 0;
      return;
    }
    this.castSlotSeen = slot;
    this.castSpan = Math.max(1, castMs);
    this.castEndAt = nowMs + castMs;
  }

  /** Milliseconds left on this player's cast; 0 = not casting. */
  castMsLeft(nowMs: number): number {
    return this.castEndAt > nowMs ? this.castEndAt - nowMs : 0;
  }

  /** 0 as the cast starts, 1 as it lands. */
  castFraction(nowMs: number): number {
    const left = this.castMsLeft(nowMs);
    if (left <= 0) return 0;
    return Math.max(0, Math.min(1, 1 - left / this.castSpan));
  }

  get castSlot(): number {
    return this.castSlotSeen;
  }

  /** The crowd slot this body draws on, or -1. Only the nameplate hooks want it. */
  get plateSlot(): number {
    return this.slot;
  }

  /** `look` bits 3..5 — the Calling that decides which ability an `Ev.Ability` slot names. */
  get calling(): number {
    return this.lookWord < 0 ? 0 : (this.lookWord >>> LOOK_CALLING_SHIFT) & LOOK_CALLING_MASK;
  }

  get renderX(): number {
    return this.rx;
  }
  get renderY(): number {
    return this.ry;
  }
  get renderZ(): number {
    return this.rz;
  }
  get renderYaw(): number {
    return this.ryaw;
  }

  /**
   * The capsule the click-to-target ray tests against, built from the *interpolated* position
   * — exactly what the player is looking at. There is no mesh to read it off any more.
   */
  hitTarget(out: HitTarget): HitTarget {
    out.id = this.id;
    out.pos.x = this.rx;
    out.pos.y = this.ry;
    out.pos.z = this.rz;
    const crouching = (this.flags & SnapFlags.Crouching) !== 0;
    // `stance` overrides `crouching` for the capsule height (see HitTarget); `crouching` is
    // kept in sync for anything still reading the legacy boolean.
    out.stance = crouching ? Stance.Crouch : Stance.Stand;
    out.crouching = crouching;
    return out;
  }

  /** Gives the crowd slot back, defaults restored. There is nothing else to dispose. */
  dispose(): void {
    if (this.crowd) releaseSlot(this.crowd, this.slot);
    this.slot = -1;
  }
}

/**
 * The kill cam's stand-in for the *local* player's body while we watch the killer's replay
 * from inside their head. It is an ordinary crowd slot — there is no ghost mesh any more, so
 * it costs one instance in meshes that were being drawn regardless.
 *
 * Deliberately not named `GhostBody`: the old type carried an `Object3D` the caller had to
 * add to the scene, and nothing about this one does.
 */
export interface GhostSlot {
  /** `anim` is an `AnimState`; the replay reads it off the recorded `PoseSample`. */
  setPose(x: number, y: number, z: number, yaw: number, anim: number): void;
  dispose(): void;
}

/**
 * DevTools handle: `__stillsunChars` reports how many remotes exist and how many of them
 * actually got a crowd slot. Purely diagnostic, and read by `tools/scripts/perf-probe.mjs`.
 * `animated` is pinned at 0 — there are no mixers left to step.
 */
const charDebug: { total: number; slotted: number; animated: number } = {
  total: 0,
  slotted: 0,
  animated: 0,
};
(globalThis as { __stillsunChars?: unknown }).__stillsunChars = charDebug;

export class RemoteManager {
  private readonly players = new Map<number, RemotePlayer>();
  private readonly names = new Map<number, string>();
  /**
   * Packed armour word per player id, from the Welcome roster and PlayerJoined. Kept only for
   * the hub's roster tiles: the *live* appearance is `PlayerSnap.look`, which unlike this one
   * changes mid-session when somebody equips something.
   */
  private readonly cosmetics = new Map<number, number>();
  private readonly crowd: CrowdField | null;
  private selfId = -1;
  /** Scratch buffer reused by `hitTargets()`. */
  private readonly targets: HitTarget[] = [];

  /**
   * `crowd` is nullable because main.ts is fail-soft about the bodies: an unreachable bake
   * warns and carries on, and everything here except the visible body — the sample ring, the
   * kill-cam history, the compass, the target capsules — works exactly the same without one.
   */
  constructor(crowd: CrowdField | null) {
    this.crowd = crowd;
  }

  get count(): number {
    return this.players.size;
  }

  setSelf(id: number): void {
    this.selfId = id;
    const mine = this.players.get(id);
    if (mine) {
      mine.dispose();
      this.players.delete(id);
    }
  }

  /** Replaces the roster (on Welcome). Keeps existing slots where ids match. */
  setRoster(list: readonly { id: number; name: string; cosmetics?: number }[]): void {
    this.names.clear();
    this.cosmetics.clear();
    for (const p of list) {
      this.names.set(p.id, p.name);
      this.cosmetics.set(p.id, p.cosmetics ?? 0);
    }
    for (const [id, rp] of this.players) {
      const name = this.names.get(id);
      if (name === undefined) {
        rp.dispose();
        this.players.delete(id);
      } else {
        rp.setName(name);
      }
    }
  }

  add(id: number, name: string, cosmetics = 0): void {
    this.names.set(id, name);
    this.cosmetics.set(id, cosmetics);
    this.players.get(id)?.setName(name);
  }

  remove(id: number): void {
    this.names.delete(id);
    this.cosmetics.delete(id);
    const rp = this.players.get(id);
    if (!rp) return;
    rp.dispose();
    this.players.delete(id);
  }

  nameOf(id: number): string {
    return this.names.get(id) ?? `#${String(id)}`;
  }

  clear(): void {
    for (const rp of this.players.values()) rp.dispose();
    this.players.clear();
    this.names.clear();
    this.cosmetics.clear();
  }

  /**
   * Kept because main.ts calls it on every Welcome, and because a `S2C.Transfer` must not
   * carry the last zone's dead into this one. There is no corpse pool any more: a dead body
   * is its own crowd slot holding the last frame of the death clip, and it goes when the
   * player does.
   */
  clearCorpses(): void {
    /* no corpses to clear — see the class header */
  }

  onSnapshot(serverTime: number, snaps: readonly PlayerSnap[]): void {
    for (const s of snaps) {
      if (s.id === this.selfId) continue; // snapshots include ourselves
      let rp = this.players.get(s.id);
      if (!rp) {
        rp = new RemotePlayer(s.id, this.nameOf(s.id), this.crowd);
        this.players.set(s.id, rp);
      }
      rp.push(serverTime, s);
    }
  }

  /**
   * One interpolation step per remote. `serverNow` is the estimated server clock in ms.
   *
   * No `dt` and no viewer any more: the clip clock, the culling, the nameplates and the
   * distance sort all live in `CrowdField.update`, which main.ts calls once a frame for
   * every character in the world at once.
   */
  update(serverNow: number): void {
    const renderTime = serverNow - INTERP_DELAY_MS;
    let slotted = 0;
    for (const [id, rp] of this.players) {
      // Absence is measured on the server clock, the same one the snapshots stamp. A player
      // who walked out of the interest radius is still on the roster, so this — not
      // `PlayerLeft` — is what gives their crowd slot back.
      if (serverNow - rp.lastSeen > STALE_MS) {
        rp.dispose();
        this.players.delete(id);
        continue;
      }
      rp.apply(renderTime);
      if (!rp.hidden) slotted += 1;
    }
    charDebug.total = this.players.size;
    charDebug.slotted = slotted;
  }

  /** Ev.Damage against a remote: the crowd's hit flash. */
  onDamage(id: number): void {
    this.players.get(id)?.onDamage();
  }

  /**
   * Ev.Cast (v3.1 §9.2): somebody else started — or, with `castMs: 0`, abandoned — a cast.
   * The bar under the target frame is the only reason a party ever learns to interrupt
   * anything, so this is kept per player rather than only for whoever happens to be selected.
   */
  onCast(id: number, slot: number, castMs: number, nowMs: number): void {
    this.players.get(id)?.onCast(slot, castMs, nowMs);
  }

  /**
   * Everything the target frame needs about another Waker, or `null` if they are not here.
   * `nowMs` is `performance.now()` — the clock `Ev.Cast`'s bar is animated against.
   */
  targetInfo(
    id: number,
    nowMs: number,
  ): {
    name: string;
    level: number;
    hp: number;
    maxHp: number;
    castMs: number;
    castFraction: number;
    castSlot: number;
    calling: number;
  } | null {
    const rp = this.players.get(id);
    if (!rp) return null;
    return {
      name: this.nameOf(id),
      level: rp.level,
      hp: rp.hp,
      maxHp: rp.maxHp,
      castMs: rp.castMsLeft(nowMs),
      castFraction: rp.castFraction(nowMs),
      castSlot: rp.castSlot,
      calling: rp.calling,
    };
  }

  /**
   * The selected player's nameplate health bar. Returns false when the id is not one of ours,
   * so the caller can try the entities instead — see `EntityManager.setTargetPlate`.
   */
  setTargetPlate(id: number): boolean {
    const rp = id === 0 ? undefined : this.players.get(id);
    if (!this.crowd || !rp || rp.plateSlot < 0) return false;
    this.crowd.setPlateHp(rp.plateSlot, Math.max(0, Math.min(1, rp.hp / Math.max(1, rp.maxHp))));
    return true;
  }

  /**
   * Ev.Revived. The get-up is `AnimState` on the next snapshot — the server picks the pose —
   * so there is nothing to trigger here; kept because main.ts calls it and because a revive
   * is exactly the sort of thing a future one-shot would hang off.
   */
  onRevived(id: number): void {
    void id;
  }

  /**
   * `Ev.Respawn` for a remote. The interpolation window still holds samples from where they
   * fell, and lerping between those and the graveyard would slide the body across the map for
   * the whole 100 ms delay — so the window is thrown away and the next snapshot snaps.
   */
  onRespawn(id: number): void {
    this.players.get(id)?.resetInterpolation();
  }

  /** The Calling of a remote (0 = unknown or none), read out of `look` bits 3..5. */
  callingOf(id: number): number {
    return this.players.get(id)?.calling ?? 0;
  }

  // ------------------------------------------------------------------ kill cam

  /** Hides one remote's body outright (the kill cam's killer). Cleared on the way out. */
  setHidden(id: number, on: boolean): void {
    this.players.get(id)?.setHidden(on);
  }

  /**
   * A crowd slot for the kill cam's replayed local body. Returns null when there is no crowd
   * or the field is full, in which case the replay simply runs without our own body in it.
   */
  createGhost(name: string, look = 0): GhostSlot | null {
    const crowd = this.crowd;
    if (!crowd) return null;
    const slot = crowd.acquire();
    if (slot < 0) return null;
    crowd.setName(slot, name);
    crowd.setLook(slot, look);
    let released = false;
    const at: Vec3 = { x: 0, y: 0, z: 0 };
    return {
      setPose(x, y, z, yaw, anim): void {
        if (released) return;
        at.x = x;
        at.y = y;
        at.z = z;
        crowd.setState(slot, at, yaw, anim);
      },
      dispose(): void {
        if (released) return;
        released = true;
        releaseSlot(crowd, slot);
      },
    };
  }

  /** Server time of the newest pose held for `id`; 0 when we have no history at all. */
  latestPoseTime(id: number): number {
    return this.players.get(id)?.latestPoseTime ?? 0;
  }

  /**
   * The poses `id` was seen at from `sinceMs` onward, copied by value into `out` (oldest
   * first) and returned as a count. Called once when a kill cam starts, never per frame.
   */
  historyOf(id: number, sinceMs: number, out: PoseSample[]): number {
    return this.players.get(id)?.copyHistory(sinceMs, out) ?? 0;
  }

  /** Rendered yaw for a remote, or `null` when we have never seen them. */
  yawOf(id: number): number | null {
    const rp = this.players.get(id);
    return rp ? rp.renderYaw : null;
  }

  /** Rendered position of one remote, or `null` if they are gone or dead. */
  positionOf(id: number): Vec3 | null {
    const rp = this.players.get(id);
    if (!rp || rp.isDead) return null;
    return { x: rp.renderX, y: rp.renderY, z: rp.renderZ };
  }

  /**
   * Every live remote's id and *rendered* position, for the HUD compass strip and the zone
   * map. Fills the caller's `out` (growing it once, then reusing its slots forever) and
   * returns how many entries are valid, so the per-frame HUD path allocates nothing.
   *
   * Deliberately not `hitTargets()`: that hands back the targeting scratch, which the click
   * ray is already holding references into for the rest of the frame.
   */
  compassTargets(out: { id: number; pos: Vec3 }[]): number {
    let n = 0;
    for (const rp of this.players.values()) {
      if (rp.isDead) continue;
      let slot = out[n];
      if (!slot) {
        slot = { id: 0, pos: { x: 0, y: 0, z: 0 } };
        out.push(slot);
      }
      slot.id = rp.id;
      slot.pos.x = rp.renderX;
      slot.pos.y = rp.renderY;
      slot.pos.z = rp.renderZ;
      n += 1;
    }
    return n;
  }

  /**
   * The nearest downed player inside `maxDist`, for the "Hold E to revive" prompt. Downed
   * players are still legal targets, so this walks the map rather than reusing the targeting
   * scratch.
   */
  nearestDowned(from: Vec3, maxDist: number): { id: number; name: string; dist: number } | null {
    let best: { id: number; name: string; dist: number } | null = null;
    for (const rp of this.players.values()) {
      if (!rp.isDowned) continue;
      const d = Math.hypot(rp.renderX - from.x, rp.renderY - from.y, rp.renderZ - from.z);
      if (d > maxDist || (best && d >= best.dist)) continue;
      best = { id: rp.id, name: this.nameOf(rp.id), dist: d };
    }
    return best;
  }

  /** Players a reveal effect should paint (see effects.ts `RevealPoint`). */
  revealPoints(out: { pos: Vec3; height: number; friendly: boolean }[], start: number): number {
    let n = start;
    for (const rp of this.players.values()) {
      if (rp.isDead) continue;
      const slot = out[n];
      if (slot) {
        slot.pos.x = rp.renderX;
        slot.pos.y = rp.renderY;
        slot.pos.z = rp.renderZ;
        slot.height = PLAYER_HEIGHT;
        slot.friendly = true;
      } else {
        out.push({
          pos: { x: rp.renderX, y: rp.renderY, z: rp.renderZ },
          height: PLAYER_HEIGHT,
          friendly: true,
        });
      }
      n += 1;
    }
    return n;
  }

  /**
   * Live target capsules at their currently *rendered* positions — exactly what the player is
   * looking at, which is what the click-to-target ray has to agree with. Built from the
   * interpolated transform, not from a mesh: there are no meshes. Dead players are excluded.
   * Reuses `this.targets` to avoid per-frame allocation.
   */
  hitTargets(): readonly HitTarget[] {
    let n = 0;
    for (const rp of this.players.values()) {
      if (rp.isDead) continue;
      let slot = this.targets[n];
      if (!slot) {
        slot = { id: 0, pos: { x: 0, y: 0, z: 0 }, crouching: false };
        this.targets.push(slot);
      }
      rp.hitTarget(slot);
      n += 1;
    }
    return n === this.targets.length ? this.targets : this.targets.slice(0, n);
  }
}
